import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  startOfWeek,
  addDays,
  subWeeks,
  isWithinInterval,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
} from 'date-fns';

import { infantSelectors } from '@/store/infant';
import { motherSelectors } from '@/store/mother';
import { InfantDto, MotherDto, VisitDto } from '@ecdlink/core';

export const weightQuestion = `Weight`;
export const lengthQuestion = `Length`;
export const muacQuestion = `What is {client} MUAC today?`;

export const useHighlights = () => {
  const infants = useSelector(infantSelectors.getInfants);
  const mothers = useSelector(motherSelectors.getMothers);

  return useMemo(() => {
    const today = new Date();

    // ── Current month ─────────────────────────────────────────
    const thisMonthStart = startOfDay(startOfMonth(today));
    const thisMonthEnd = endOfDay(endOfMonth(today));
    const thisMonthInterval = { start: thisMonthStart, end: thisMonthEnd };

    // This week (Monday → Sunday) — full day boundaries
    const thisWeekMonday = startOfDay(startOfWeek(today, { weekStartsOn: 1 }));
    const thisWeekSunday = endOfDay(addDays(thisWeekMonday, 6));
    const thisWeekInterval = { start: thisWeekMonday, end: thisWeekSunday };

    // Last week
    const lastWeekStart = startOfDay(
      startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 })
    );
    const lastWeekEnd = endOfDay(addDays(lastWeekStart, 6));
    const lastWeekInterval = { start: lastWeekStart, end: lastWeekEnd };

    // ── Helpers ───────────────────────────────────────────────

    const getUniqueInfantsWithGrowth = (
      visits: VisitDto[],
      interval: { start: Date; end: Date }
    ): number => {
      const clientIdsWithGrowth = new Set<string>();

      visits.forEach((visit) => {
        if (!isAttendedInInterval(visit, interval)) return;
        if (!visit.clientId) return; // safety check
        if (!hasGrowthMeasurement(visit)) return;

        clientIdsWithGrowth.add(visit.clientId);
      });

      return clientIdsWithGrowth.size;
    };

    const isInInterval = (
      dateStr: string | null | undefined,
      interval: { start: Date; end: Date }
    ) => {
      if (!dateStr) return false;
      const date = startOfDay(new Date(dateStr));
      return isWithinInterval(date, interval);
    };

    const isAttendedInInterval = (
      visit: VisitDto,
      interval: { start: Date; end: Date }
    ) => {
      if (!visit.attended || !visit.actualVisitDate) return false;
      const visitDate = startOfDay(new Date(visit.actualVisitDate));
      return isWithinInterval(visitDate, interval);
    };

    const hasGrowthMeasurement = (visit: VisitDto): boolean => {
      if (!visit.visitAnswers?.length) return false;
      const growthQuestions = new Set([
        weightQuestion,
        lengthQuestion,
        muacQuestion,
      ]);
      return visit.visitAnswers.some(
        (answer) =>
          answer?.question && growthQuestions.has(answer.question.trim())
      );
    };

    const countVisits = (
      visits: VisitDto[] = [],
      interval: { start: Date; end: Date },
      filterFn?: (v: VisitDto) => boolean
    ) =>
      visits.reduce((count, visit) => {
        if (!isAttendedInInterval(visit, interval)) return count;
        return count + (filterFn ? Number(filterFn(visit)) : 1);
      }, 0);

    // ── Data preparation ──────────────────────────────────────

    const allMotherVisits = mothers?.flatMap((m) => m?.visits ?? []) ?? [];
    const allInfantVisits = infants?.flatMap((i) => i?.visits ?? []) ?? [];
    const infantVisitsWithAnswers = allInfantVisits.filter(
      (v) => v?.visitAnswers?.length
    );

    // ── Family visits attended ────────────────────────────────
    const totalThisWeekFamilyVisits =
      countVisits(allMotherVisits, thisWeekInterval) +
      countVisits(allInfantVisits, thisWeekInterval);

    const totalLastWeekFamilyVisits =
      countVisits(allMotherVisits, lastWeekInterval) +
      countVisits(allInfantVisits, lastWeekInterval);

    // ── Growth monitored (only infants usually) ───────────────
    const totalThisWeekGrowthMonitored = getUniqueInfantsWithGrowth(
      infantVisitsWithAnswers,
      thisWeekInterval
    );

    const totalLastWeekGrowthMonitored = getUniqueInfantsWithGrowth(
      infantVisitsWithAnswers,
      lastWeekInterval
    );

    // ── New clients (mothers + infants registered this week) ──
    const isNewThisWeek = (entity: MotherDto | InfantDto) =>
      isInInterval(entity?.insertedDate?.toString(), thisWeekInterval);

    const isNewLastWeek = (entity: MotherDto | InfantDto) =>
      isInInterval(entity?.insertedDate?.toString(), lastWeekInterval);

    const totalThisWeekNewClients =
      (mothers?.filter(isNewThisWeek) ?? []).length +
      (infants?.filter(isNewThisWeek) ?? []).length;

    const totalLastWeekNewClients =
      (mothers?.filter(isNewLastWeek) ?? []).length +
      (infants?.filter(isNewLastWeek) ?? []).length;

    // ── Total infants for current month ───────────────────────
    const totalInfantsForMonth =
      infants?.filter((infant) =>
        isInInterval(infant?.insertedDate?.toString(), thisMonthInterval)
      ).length ?? 0;

    const totalMothersForMonth =
      mothers?.filter((mother) =>
        isInInterval(mother?.insertedDate?.toString(), thisMonthInterval)
      ).length ?? 0;

    return {
      totalThisWeekFamilyVisits,
      totalLastWeekFamilyVisits,

      totalThisWeekGrowthMonitored,
      totalLastWeekGrowthMonitored,

      totalThisWeekNewClients,
      totalLastWeekNewClients,

      totalInfantsForMonth,
      totalMothersForMonth,
    };
  }, [infants, mothers]);
};
